import React, { useState, useEffect } from 'react';

const AdminDashboard = ({ apiEndpoints }) => {
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [icdStatus, setIcdStatus] = useState({});
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    } else if (activeTab === 'icd') {
      fetchIcdStatus();
    }
  }, [activeTab]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await fetch(apiEndpoints.users, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      } else {
        console.error('Failed to fetch users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchIcdStatus = async () => {
    setLoading(true);
    try {
      const response = await fetch(apiEndpoints.icd_codes, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setIcdStatus(data);
      } else {
        console.error('Failed to fetch ICD status');
      }
    } catch (error) {
      console.error('Error fetching ICD status:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleAdmin = async (userId) => {
    try {
      const url = apiEndpoints.toggle_admin.replace(':id', userId);
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      });
      
      if (response.ok) {
        const { user } = await response.json();
        setUsers(users.map(u => u.id === user.id ? user : u));
        setMessage({ type: 'success', text: `Admin status ${user.admin ? 'granted to' : 'revoked from'} ${user.name}` });
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.error || 'Failed to update admin status' });
      }
    } catch (error) {
      console.error('Error toggling admin status:', error);
      setMessage({ type: 'error', text: 'An error occurred' });
    }
  };

  const refreshIcdCodes = async () => {
    try {
      const response = await fetch(apiEndpoints.refresh_icd, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMessage({ type: 'success', text: data.message });
        fetchIcdStatus();
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.error || 'Failed to refresh ICD codes' });
      }
    } catch (error) {
      console.error('Error refreshing ICD codes:', error);
      setMessage({ type: 'error', text: 'An error occurred' });
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await fetch(apiEndpoints.upload_icd, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        setMessage({ type: 'success', text: data.message });
        fetchIcdStatus();
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.error || 'Failed to upload ICD codes' });
      }
    } catch (error) {
      console.error('Error uploading ICD codes:', error);
      setMessage({ type: 'error', text: 'An error occurred' });
    }
  };

  return (
    <div className="admin-dashboard">
      <h1 style={{ fontSize: '1.75rem', marginBottom: '1.5rem' }}>Admin Dashboard</h1>
      
      {message && (
        <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-danger'}`} 
             style={{ marginBottom: '1rem' }}>
          {message.text}
          <button type="button" className="close" onClick={() => setMessage(null)}>
            <span>&times;</span>
          </button>
        </div>
      )}
      
      <div className="admin-tabs" style={{ marginBottom: '1.5rem' }}>
        <ul className="nav nav-tabs">
          <li className="nav-item">
            <a className={`nav-link ${activeTab === 'users' ? 'active' : ''}`}
               onClick={() => setActiveTab('users')}
               href="#">
              <i className="fas fa-users" style={{ marginRight: '0.5rem' }}></i>
              User Management
            </a>
          </li>
          <li className="nav-item">
            <a className={`nav-link ${activeTab === 'icd' ? 'active' : ''}`}
               onClick={() => setActiveTab('icd')}
               href="#">
              <i className="fas fa-sync" style={{ marginRight: '0.5rem' }}></i>
              ICD Code Management
            </a>
          </li>
        </ul>
      </div>
      
      {loading ? (
        <div className="text-center py-5">
          <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem' }}></i>
          <p>Loading...</p>
        </div>
      ) : activeTab === 'users' ? (
        <div className="user-management">
          <table className="table table-striped">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Admin</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id}>
                  <td>{user.name}</td>
                  <td>{user.email}</td>
                  <td>
                    <span className={`badge ${user.admin ? 'bg-success' : 'bg-secondary'}`}>
                      {user.admin ? 'Admin' : 'User'}
                    </span>
                  </td>
                  <td>
                    <button
                      className={`btn btn-sm ${user.admin ? 'btn-danger' : 'btn-success'}`}
                      onClick={() => toggleAdmin(user.id)}
                    >
                      {user.admin ? 'Revoke Admin' : 'Make Admin'}
                    </button>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan="4" className="text-center">No users found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="icd-management">
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">ICD Code Database</h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-4">
                  <strong>Last Refresh:</strong> {icdStatus.last_refresh || 'N/A'}
                </div>
                <div className="col-md-4">
                  <strong>Total Codes:</strong> {icdStatus.total_codes || 0}
                </div>
                <div className="col-md-4">
                  <strong>Status:</strong> {icdStatus.status || 'Unknown'}
                </div>
              </div>
            </div>
          </div>
          
          <div className="row">
            <div className="col-md-6">
              <div className="card">
                <div className="card-header">
                  <h5 className="mb-0">Upload ICD Codes File</h5>
                </div>
                <div className="card-body">
                  <p>Upload a CSV or Excel file with ICD codes to update the database.</p>
                  <div className="custom-file">
                    <input 
                      type="file" 
                      className="custom-file-input" 
                      id="icdFile" 
                      accept=".csv,.xlsx,.xls"
                      onChange={handleFileUpload}
                    />
                    <label className="custom-file-label" htmlFor="icdFile">
                      Choose file
                    </label>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="col-md-6">
              <div className="card">
                <div className="card-header">
                  <h5 className="mb-0">Refresh from API</h5>
                </div>
                <div className="card-body">
                  <p>Fetch the latest ICD-10 codes from the official data source.</p>
                  <button
                    className="btn btn-primary"
                    onClick={refreshIcdCodes}
                  >
                    <i className="fas fa-sync" style={{ marginRight: '0.5rem' }}></i>
                    Refresh ICD Codes
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard; 
